import { useState, useEffect, memo, useImperativeHandle, forwardRef, useRef, useCallback, useMemo } from "react";
import CommentField from "./CommentField";
import { Divider, Typography, Row, Col, Checkbox, Spin, Tooltip, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, useFormikContext } from "formik";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import utc from "dayjs/plugin/utc";
import { useNavigate } from "react-router-dom";
import timezone from "dayjs/plugin/timezone";
import { Starttask } from "../../../Slices/timerslice";
import { updateWorkHistoryThunk } from "../../../Slices/orderSlice";
import { addLastContactedDate } from "../../../Slices/lastContactedDatesSlice";
import { setIsRefetch } from "../../../Slices/salesSlice";
import { getOptionsByPath } from "../../../utilsdata";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { summarizeCallNotesThunk, transcribeRecordingThunk } from "../../../Slices/aiSalesSlice";
import { SERVER_URL } from "../../../databucket";

import {
  InputField,
  TimePickerField,
  SelectInput,
  AntdButton,
  TextareaField,
  DatePickerField,
  SwitchField,

} from "../../../blueplanitUtils/InputField";
import { CALL_STATUS, CALLING_TRACK } from "../../../Dropdownmanager";

dayjs.extend(utc);
dayjs.extend(timezone);


const SENTIMENT_STYLE = {
  positive: { bg: '#dcfce7', text: '#15803d', label: 'Positive' },
  neutral:  { bg: '#fef9c3', text: '#854d0e', label: 'Neutral'  },
  negative: { bg: '#fee2e2', text: '#b91c1c', label: 'Negative' },
};

function StarRating({ rating }) {
  if (!rating) return null;
  const full = Math.round(rating / 2); // convert 1-10 to 1-5 stars
  return (
    <span style={{ fontSize: 14, letterSpacing: 1 }}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} style={{ color: i < full ? '#f59e0b' : '#d1d5db' }}>★</span>
      ))}
      <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 5 }}>{rating}/10</span>
    </span>
  );
}

function CoachingPanel({ coachingData }) {
  const { callRating, coachingFeedback, sentiment, structuredSummary, transcript } = coachingData;
  const sentStyle = SENTIMENT_STYLE[sentiment] || SENTIMENT_STYLE.neutral;

  return (
    <div style={{
      margin: '8px 0 4px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: '10px 14px',
      fontSize: 12,
    }}>
      {/* Header row: rating + sentiment */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: '#374151', fontSize: 12 }}>AI Analysis</span>
        <StarRating rating={callRating} />
        <span style={{
          background: sentStyle.bg, color: sentStyle.text,
          padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
        }}>
          {sentStyle.label}
        </span>
      </div>

      {/* Summary */}
      {structuredSummary && (
        <div style={{ color: '#374151', marginBottom: 8, lineHeight: 1.5 }}>
          {structuredSummary}
        </div>
      )}

      {/* Coaching feedback */}
      {coachingFeedback?.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, color: '#6d28d9', marginBottom: 4 }}>💡 Coaching</div>
          <ul style={{ margin: 0, paddingLeft: 16, color: '#4b5563', lineHeight: 1.7 }}>
            {coachingFeedback.map((tip, i) => <li key={i}>{tip}</li>)}
          </ul>
        </div>
      )}

      {/* Transcript toggle */}
      {transcript && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: 11 }}>View transcript</summary>
          <div style={{
            marginTop: 6, maxHeight: 120, overflowY: 'auto',
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: 4, padding: '6px 8px', color: '#374151', lineHeight: 1.6,
          }}>
            {transcript}
          </div>
        </details>
      )}
    </div>
  );
}

const CallRow = memo(({
  index,
  callInfo,
  spokeWith,
  remarks,
  callStatusOptions,
  userOptions,
  hasRingCentralIframe,
  onSpokeWithChange,
  onRemarksChange,
  onStatusChange,
  onCallByChange,
  emailSent,
  onEmailSentChange,
  callStatus,
  onPlayAudio,
  onAudioError,
  activeAudioIndex,
  secureUrls,
  getDateValue,
  getTime,
  handleChange,
  setFieldValue,
  loginUserFullName,
  onSummarize,
  isSummarizing,
  coachingData,
}) => {

  return (
    <div className="call-row">
      <div className="call-wrapper">

        {/* Floating Call Number */}
        <div className="call-index">
          <i className="fa-solid fa-phone"></i>
          <strong>{index + 1}</strong>
        </div>

        <Row gutter={8}>
          {/* LEFT CARD */}
          <Col span={12}>
            <div className="left-card">

              {/* Row 1 */}
              <div className="row-line space-between mb-10">
                <div className="row-line gap-6">
                  <i className="fa-solid fa-phone"></i>
                  <span>{callInfo.from || "N/A"}</span>
                </div>

                <div className="row-line gap-6 nowrap">
                  <i className="fa-regular fa-clock"></i>
                  <span>
                    {getDateValue(callInfo?.date)?.format("DD/MM/YYYY")}{" "}
                    {getTime(callInfo?.time)?.format("h:mm A")}
                  </span>
                </div>
              </div>

              {/* Row 2 */}
              <div className="row-line space-between mb-10">
                <div className="row-line gap-6">
                  <i className="fa-solid fa-phone-volume"></i>
                  <span>{callInfo.to || "N/A"}</span>
                </div>

                <div className="row-line gap-6">
                  <i className="fa-solid fa-user"></i>
                  <span className="nowrap">{callInfo?.callby || "N/A"}</span>
                </div>
              </div>

              {/* Row 3 */}
              <div className="row-line space-between">
                <div className="row-line gap-6">
                  <i className="fa-solid fa-stopwatch"></i>
                  <span>
                    {callInfo.duration
                      ? `${Math.floor(callInfo.duration / 60)}m ${callInfo.duration % 60}s`
                      : "N/A"}
                  </span>
                </div>

                <div className="row-line gap-6">
                  <i className="fa-solid fa-microphone"></i>

                  {callInfo?.recordingid ? (
                    activeAudioIndex === index && secureUrls[index] ? (
                      <audio controls className="audio-player">
                        <source src={secureUrls[index]} type="audio/mpeg" />
                      </audio>
                    ) : secureUrls[index] === "ERROR" ? (
                      <Typography.Text type="danger">Authorize</Typography.Text>
                    ) : (
                      <AntdButton
                        label="Play"
                        type="link"
                        onClick={() => onPlayAudio(index, callInfo.recordingid)}
                      />
                    )
                  ) : (
                    <Typography.Text type="secondary">No Audio</Typography.Text>
                  )}
                </div>
              </div>

            </div>
          </Col>

          {/* RIGHT CARD */}
          <Col span={12}>
            <div className="right-card">

              <Row gutter={16} className="row-align-center">
                <Col span={8}>
                  <SelectInput
                    label="Call Status"
                    value={callStatus || ""}
                    onChange={(value) => onStatusChange(value, index)}
                    options={callStatusOptions}
                    placeholder="Select"
                  />
                </Col>

                <Col span={8} className="spoke-top">
                  <InputField
                    label="Spoke With"
                    value={spokeWith || ""}
                    onChange={(e) => onSpokeWithChange(e, index)}
                  />
                </Col>

                <Col span={8}>
                  <div className="email-block">
                    <label>Email Sent</label>
                <Checkbox
  checked={emailSent}
  onChange={(e) => onEmailSentChange(e.target.checked, index)}
/>
                  </div>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24} className="remarks-top">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <TextareaField
                        label="Remarks"
                        value={remarks || ""}
                        onChange={(e) => onRemarksChange(e, index)}
                        rows={1}
                      />
                    </div>
                    <Tooltip title={callInfo?.recordingid ? "Transcribe recording & analyse with AI" : "Summarise notes with AI"}>
                      <button
                        type="button"
                        disabled={isSummarizing || (!callInfo?.recordingid && !remarks?.trim())}
                        onClick={() => onSummarize(index)}
                        style={{
                          marginTop: 22,
                          background: isSummarizing ? '#e9d5ff' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          padding: '5px 10px',
                          cursor: isSummarizing || (!callInfo?.recordingid && !remarks?.trim()) ? 'not-allowed' : 'pointer',
                          opacity: (!callInfo?.recordingid && !remarks?.trim()) ? 0.5 : 1,
                          whiteSpace: 'nowrap',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                        }}
                      >
                        {isSummarizing
                          ? <><Spin size="small" />&nbsp;AI...</>
                          : <><FontAwesomeIcon icon={faWandMagicSparkles} /> {callInfo?.recordingid ? 'Transcribe' : 'AI'}</>
                        }
                      </button>
                    </Tooltip>
                  </div>
                  {/* Coaching panel — shown after AI analysis */}
                  {coachingData && <CoachingPanel coachingData={coachingData} />}
                </Col>
              </Row>

            </div>
          </Col>
        </Row>
      </div>

      <Divider className="call-divider" />
    </div>
  );

});

CallRow.displayName = 'CallRow';

const CallingTrack = memo(
  forwardRef((props, ref) => {
    const { SalesInfo, assignToUsers, setFieldValue, id, leadID } = props;
    const { values, handleChange, submitForm } = useFormikContext();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const arrayHelpersRef = useRef(null);

    // Redux selectors - memoized
    const { task, requestId: WorkingId } = useSelector((state) => state.timer);
    const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
    const salesLeadId = leadID;
    const isRefetch = useSelector((state) => state.sales.isRefetch);
    const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);

    const userId = loginUser?.id;
    const loginUserFullName = useMemo(
      () => `${loginUser?.firstname || ''} ${loginUser?.lastname || ''}`.trim(),
      [loginUser?.firstname, loginUser?.lastname]
    );

    // Local state
    const [phoneNumber, setPhoneNumber] = useState("");
    const [activeAudioIndex, setActiveAudioIndex] = useState(null);
    const [secureUrls, setSecureUrls] = useState({});
    const [spokeWith, setSpokeWith] = useState([]);
    const [callStatus, setcallStatus] = useState([]);
    const [remarks, setRemarks] = useState([]);
    const [emailSent, setEmailSent] = useState([]);
    const [summarizingIndex, setSummarizingIndex] = useState(null);
    // Per-call coaching data from AI: { [index]: { callRating, coachingFeedback, sentiment, structuredSummary, transcript } }
    const [callCoaching, setCallCoaching] = useState({});
    const prevLengthRef = useRef(0);
    const syncTimeoutRef = useRef(null);
    const pendingUpdatesRef = useRef({});

    // Initialize local state from Formik values
    useEffect(() => {
      const calls = values?.leadsCalls || [];
      setSpokeWith(calls.map((call) => call?.spokeWith || ''));
      setcallStatus(calls.map((call) => call?.status || ''));
      setRemarks(calls.map((call) => call?.remarks || ''));
      setEmailSent(calls.map((call) => !!call?.emailSent));
      prevLengthRef.current = calls.length;
    }, []);

    // Handle refetch
    useEffect(() => {
      if (isRefetch) {
        const calls = values?.leadsCalls || [];
        setSpokeWith(calls.map((call) => call?.spokeWith || ''));
        setcallStatus(calls.map((call) => call?.status || ''));
        setRemarks(calls.map((call) => call?.remarks || ''));
        setEmailSent(calls.map((call) => !!call?.emailSent));
        dispatch(setIsRefetch(false));
      }
    }, [isRefetch, values?.leadsCalls, dispatch]);

    // Sync when new rows are added
    useEffect(() => {
      const currentLength = values?.leadsCalls?.length || 0;
      if (currentLength > prevLengthRef.current) {
        setSpokeWith((prev) => {
          const newArray = [...prev];
          for (let i = prev.length; i < currentLength; i++) {
            newArray[i] = values.leadsCalls[i]?.spokeWith || '';
          }
          return newArray;
        });
        setRemarks((prev) => {
          const newArray = [...prev];
          for (let i = prev.length; i < currentLength; i++) {
            newArray[i] = values.leadsCalls[i]?.remarks || '';
          }
          return newArray;
        });
        setcallStatus((prev) => {
          const newArray = [...prev];
          for (let i = prev.length; i < currentLength; i++) {
            newArray[i] = values.leadsCalls[i]?.status || '';
          }
          return newArray;
        });
        if (currentLength > prevLengthRef.current) {
    setEmailSent((prev) => {
      const newArray = [...prev];
      for (let i = prev.length; i < currentLength; i++) {
        newArray[i] = !!values.leadsCalls[i]?.emailSent;
      }
      return newArray;
    });
  }
      }
      prevLengthRef.current = currentLength;
    }, [values?.leadsCalls?.length]);

    // ✅ OPTIMIZED: Debounced sync to Formik (100ms)
useEffect(() => {
  if (syncTimeoutRef.current) {
    clearTimeout(syncTimeoutRef.current);
  }

  syncTimeoutRef.current = setTimeout(() => {
    spokeWith.forEach((value, index) => {
      if (values?.leadsCalls?.[index]?.spokeWith !== value) {
        setFieldValue(`leadsCalls[${index}].spokeWith`, value, false);
      }
    });

    remarks.forEach((value, index) => {
      if (values?.leadsCalls?.[index]?.remarks !== value) {
        setFieldValue(`leadsCalls[${index}].remarks`, value, false);
      }
    });

    callStatus.forEach((value, index) => {
      if (values?.leadsCalls?.[index]?.status !== value) {
        setFieldValue(`leadsCalls[${index}].status`, value, false);
      }
    });

    emailSent.forEach((value, index) => {
      if (values?.leadsCalls?.[index]?.emailSent !== value) {
        setFieldValue(`leadsCalls[${index}].emailSent`, value, false);
      }
    });

  }, 100);

  return () => clearTimeout(syncTimeoutRef.current);
}, [spokeWith, remarks, callStatus, emailSent, values?.leadsCalls, setFieldValue]);

    // Safety net: flush ALL pending changes to Formik immediately on unmount
    // (when navigating away from the entire lead page)
    useEffect(() => {
      return () => {
        clearTimeout(syncTimeoutRef.current);
        // Flush immediately using refs to avoid stale closure
        const sw = spokeWith, rm = remarks, cs = callStatus, es = emailSent;
        sw.forEach((value, index) => {
          if (values?.leadsCalls?.[index]?.spokeWith !== value) setFieldValue(`leadsCalls[${index}].spokeWith`, value, false);
        });
        rm.forEach((value, index) => {
          if (values?.leadsCalls?.[index]?.remarks !== value) setFieldValue(`leadsCalls[${index}].remarks`, value, false);
        });
        cs.forEach((value, index) => {
          if (values?.leadsCalls?.[index]?.status !== value) setFieldValue(`leadsCalls[${index}].status`, value, false);
        });
        es.forEach((value, index) => {
          if (values?.leadsCalls?.[index]?.emailSent !== value) setFieldValue(`leadsCalls[${index}].emailSent`, value, false);
        });
      };
    }, [spokeWith, remarks, callStatus, emailSent, values?.leadsCalls, setFieldValue]);

    // ✅ OPTIMIZED: Memoized input handlers
    const handleInputChange = useCallback((e, index) => {
      const { value } = e.target;
      setSpokeWith((prev) => {
        const newArray = [...prev];
        newArray[index] = value;
        return newArray;
      });
    }, []);

    const handleRemarksChange = useCallback((e, index) => {
      const { value } = e.target;
      setRemarks((prev) => {
        const newArray = [...prev];
        newArray[index] = value;
        return newArray;
      });
    }, []);

const handleStatusChange = useCallback((value, index) => {
  setcallStatus((prev) => {
    const newArray = [...prev];
    newArray[index] = value;
    return newArray;
  });
}, []);


    const handleCallByChange = useCallback((value, index) => {
      setFieldValue(`leadsCalls.${index}.callby`, value, false);
    }, [setFieldValue]);

 const handleEmailSentChange = useCallback((checked, index) => {
  setEmailSent((prev) => {
    const newArray = [...prev];
    newArray[index] = checked;
    return newArray;
  });
}, []);


    const handleAddRow = useCallback((arrayHelpers) => {
      const newDate = dayjs();
      setFieldValue("lastContactedDate", newDate);

      arrayHelpers.push({
        date: newDate,
        time: dayjs().set("second", 0).set("millisecond", 0).toString(),
        status: "Attempt",
        spokeWith: "",
        callby: loginUserFullName,
        emailSent: false,
        remarks: "",
        recordingid: '',
        from: "",
        to: phoneNumber || "",
      });
      dispatch(addLastContactedDate(newDate.toISOString()));
    }, [phoneNumber, loginUserFullName, setFieldValue, dispatch]);

    const handleSummarize = useCallback(async (index) => {
      const callInfo = values?.leadsCalls?.[index];
      const rawNotes = remarks[index];
      const recordingid = callInfo?.recordingid;
      const hasRecording = !!recordingid;

      if (!hasRecording && !rawNotes?.trim()) return;

      if (hasRecording) {
        const rcAccessToken = localStorage.getItem('rc_access_token');
        if (!rcAccessToken) {
          message.error('RingCentral session expired. Please re-authenticate on the RingCentral page.');
          return;
        }
      }

      setSummarizingIndex(index);
      try {
        const callData = {
          status: callStatus[index],
          spokeWith: spokeWith[index],
          duration: callInfo?.duration,
        };
        const leadContext = {
          agencyName: SalesInfo?.agencyName || '',
          city: SalesInfo?.city || '',
          state: SalesInfo?.state || '',
          previousCallCount: (values?.leadsCalls?.length || 1) - 1,
        };

        let result;
        if (hasRecording) {
          const rcAccessToken = localStorage.getItem('rc_access_token');
          result = await dispatch(
            transcribeRecordingThunk({ recordingUrl: recordingid, rcAccessToken, callData, leadContext })
          );
        } else {
          result = await dispatch(
            summarizeCallNotesThunk({ rawNotes, callData, leadContext })
          );
        }

        const isSuccess = hasRecording
          ? transcribeRecordingThunk.fulfilled.match(result)
          : summarizeCallNotesThunk.fulfilled.match(result);

        if (isSuccess) {
          const { structuredSummary, recommendedNextAction, recommendedFollowupDays,
                  callRating, coachingFeedback, sentiment, transcript } = result.payload;

          const enhanced = `${structuredSummary}${recommendedNextAction ? `\n\nNext action: ${recommendedNextAction} (in ${recommendedFollowupDays} days)` : ''}`;
          setRemarks((prev) => {
            const next = [...prev];
            next[index] = enhanced;
            return next;
          });

          setCallCoaching((prev) => ({
            ...prev,
            [index]: { callRating, coachingFeedback, sentiment, structuredSummary, transcript: transcript || null },
          }));
        } else {
          const errMsg = result?.payload || 'AI analysis failed. Please try again.';
          console.error('AI summarize error:', errMsg);
          message.error(typeof errMsg === 'string' ? errMsg : 'AI analysis failed. Please try again.');
        }
      } catch (err) {
        console.error('handleSummarize exception:', err);
        message.error('Unexpected error during AI analysis.');
      } finally {
        setSummarizingIndex(null);
      }
    }, [remarks, callStatus, spokeWith, values?.leadsCalls, SalesInfo, dispatch]);

    const handleStartButtonClick = useCallback(async (arrayHelpers, phoneNumber) => {
      if (!phoneNumber) return;

      try {
        const iframe = document.querySelector("#rc-widget-adapter-frame");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "rc-adapter-new-call",
              phoneNumber: phoneNumber,
              toCall: true,
            },
            "*"
          );
        } else {
          console.warn("RingCentral iframe not ready yet.");
        }

        await handleAddRow(arrayHelpers);
        submitForm();
      } catch (err) {
        console.error("Call failed:", err);
      }

      const currentLeadID = SalesInfo?.leadID || salesLeadId;
      const payload = {
        department: "Sales",
        task: "Calling",
        time: Date.now(),
        requestId: currentLeadID,
      };

      try {
        const variablesObject = {
          userId,
          emailId: SalesInfo?.id || '',
          requestId: currentLeadID?.toString(),
          clientName: "Blueplanit",
          processType: "",
          taskType: "Calling",
          email: "",
          subject: "",
          body: "",
          startTime: Date.now().toString(),
          endTime: "",
          department: "Sales",
        };
        await dispatch(updateWorkHistoryThunk(variablesObject)).unwrap();
      } catch (err) {
        console.error("Error updating work history:", err);
      }

      const timestamp = Date.now();
      localStorage.setItem("time", timestamp);
      localStorage.setItem("task", "Calling");
      localStorage.setItem("department", "Sales");
      localStorage.setItem("requestId", currentLeadID);
      dispatch(Starttask(payload));
    }, [SalesInfo, salesLeadId, userId, handleAddRow, submitForm, dispatch]);

    useImperativeHandle(ref, () => ({
      handleStartButtonClick,
      // Getter: the FieldArray render sets arrayHelpersRef after this handle is built.
      get arrayHelpers() { return arrayHelpersRef.current; },
    }), [handleStartButtonClick]);

    const getTime = useCallback((time) => {
      if (!time) return null;
      if (typeof time === "object" && dayjs.isDayjs(time)) return time;
      if (typeof time === "string") {
        if (time.includes("T")) return dayjs.utc(time).local();
        const today = dayjs().format("YYYY-MM-DD");
        return dayjs(`${today}T${time}`);
      }
      return null;
    }, []);

    const getDateValue = useCallback((datevalue) => {
      if (!datevalue || datevalue === "") return null;
      if (typeof datevalue === "string" && datevalue.length === 13) {
        return dayjs(parseInt(datevalue));
      }
      if (typeof datevalue === "object") return datevalue;
      return null;
    }, []);

    const handlePlayAudio = useCallback(async (index, recordingid) => {
      try {
        // Use backend proxy — it authenticates with RC service account token
        // so recordings work even if user's token has expired
        const proxyUrl = `${SERVER_URL}/api/recording-proxy?url=${encodeURIComponent(recordingid)}`;
        const res = await fetch(proxyUrl);
        if (!res.ok) {
          // Fallback: try direct URL with user's token (legacy behavior)
          const token = localStorage.getItem("rc_access_token");
          if (token) {
            const directUrl = `${recordingid}?access_token=${token}`;
            const directRes = await fetch(directUrl);
            if (directRes.ok) {
              setSecureUrls((prev) => ({ ...prev, [index]: directUrl }));
              setActiveAudioIndex(index);
              return;
            }
          }
          throw new Error("Failed to load recording");
        }

        setSecureUrls((prev) => ({ ...prev, [index]: proxyUrl }));
        setActiveAudioIndex(index);
      } catch (err) {
        setSecureUrls((prev) => ({ ...prev, [index]: "ERROR" }));
      }
    }, []);

    const handleAudioError = useCallback((index) => {
      setSecureUrls((prev) => ({ ...prev, [index]: null }));
      setActiveAudioIndex(null);
    }, []);

    // Memoized options
    const callStatusOptions = useMemo(
      () => getOptionsByPath(NestedDatabucket, [CALLING_TRACK, CALL_STATUS]),
      [NestedDatabucket]
    );

    const userOptions = useMemo(
      () => assignToUsers?.map((user) => ({
        key: user.id,
        label: `${user.firstname} ${user.lastname}`,
        value: `${user.firstname} ${user.lastname}`,
      })) || [],
      [assignToUsers]
    );

    // Poll for the RC widget iframe — it loads asynchronously after the
    // RingCentralProvider injects the adapter script, so a one-shot useMemo
    // at mount time caches `false` and the "Please Login" banner sticks even
    // after the user is actually logged in.
    const [hasRingCentralIframe, setHasRingCentralIframe] = useState(
      () => !!document.querySelector("#rc-widget-adapter-frame")
    );
    useEffect(() => {
      if (hasRingCentralIframe) return;
      const interval = setInterval(() => {
        if (document.querySelector("#rc-widget-adapter-frame")) {
          setHasRingCentralIframe(true);
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }, [hasRingCentralIframe]);

    const showCallButton = id !== undefined && !(WorkingId === leadID && task === "Calling");

    return (
      <div className="calling-track-root">
        <>
          <div className="calling-header">
            <FieldArray
              name="leadsCalls"
              render={(arrayHelpers) => {
                arrayHelpersRef.current = arrayHelpers;

                return (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        padding: "10px",
                        marginBottom: "8px",
                      }}
                    >
                      <div className="calling-track-container">
                        <InputField
                          className="calling-track-input"
                          type="Number"
                          placeholder="Enter phone number"
                          value={phoneNumber}
                          onChange={(e) =>
                            setPhoneNumber(e.target.value?.trim())
                          }
                        />

                        {showCallButton && (
                          hasRingCentralIframe ? (
                            <AntdButton
                              label="Call"
                              color="primary"
                              variant="solid"
                              onClick={() =>
                                handleStartButtonClick(arrayHelpers, phoneNumber)
                              }
                            />
                          ) : (
                            <Typography.Text
                              className="custom-rc-warning"
                              onClick={() => navigate("/ringcentral")}
                            >
                              Please Login to RingCentral to make calls
                            </Typography.Text>
                          )
                        )}
                      </div>
                    </div>
                       <div className="calling-comments-wrapper">
              <CommentField
                label="Calling Comments"
                name="callingComments"
              />
            </div>
<Row justify="end" className="add-row-wrapper">
                      <AntdButton
                        label={<FontAwesomeIcon icon={faPlus} />}
                        type="primary"
                        className="add-row-small-btn"
                        size="small"
                        onClick={() => handleAddRow(arrayHelpers)}
                      />
                    </Row>
<div className="calling-scroll-container">
                    {values?.leadsCalls?.length > 0 && (
                      <div className="calls-content">
                        {[...values.leadsCalls]
                          .map((callInfo, index) => ({ callInfo, index }))
                          .reverse()
                          .map(({ callInfo, index }) => (
                            <CallRow
                              key={`call-${index}`}
                              index={index}
                              callInfo={callInfo}
                              spokeWith={spokeWith[index]}
                              remarks={remarks[index]}
                              callStatusOptions={callStatusOptions}
                              userOptions={userOptions}
                              hasRingCentralIframe={hasRingCentralIframe}
                              onSpokeWithChange={handleInputChange}
                              onRemarksChange={handleRemarksChange}
                              callStatus={callStatus[index]}
                              onStatusChange={handleStatusChange}
                              onCallByChange={handleCallByChange}
                              onEmailSentChange={handleEmailSentChange}
                              onPlayAudio={handlePlayAudio}
                              onAudioError={handleAudioError}
                              activeAudioIndex={activeAudioIndex}
                              secureUrls={secureUrls}
                              getDateValue={getDateValue}
                              getTime={getTime}
                              handleChange={handleChange}
                              setFieldValue={setFieldValue}
                              loginUserFullName={loginUserFullName}
                              emailSent={emailSent[index]}
                              onSummarize={handleSummarize}
                              isSummarizing={summarizingIndex === index}
                              coachingData={callCoaching[index] || null}
                            />
                          ))}
                      </div>
                    )}

                    </div>
                  </>
                );
              }}
            />
            
          </div>
        </>
      </div>
    );

  })
);

CallingTrack.displayName = "CallingTrack";

export default CallingTrack;